import classnames from 'classnames'
import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react'

import { Image, ImageProps } from '@src/quince-components/src'
import { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import productCard from '@src/quince-components/src/molecules/productCard'
import { SnapScrollHandle, SnapScroll } from '@src/quince-components/src/molecules/snap-scroll'
import { MAX_DESKTOP_WIDTH, MAX_MOBILE_WIDTH } from '@src/quince-site-common/src/constants/app'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { TAG_POSITION } from '@src/quince-site-common/src/utils/constants'
import { GenderName } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'

import { ProductCardAddToWishlist } from '../product-card-actions/add-to-wishlist'
import { ProductCardBuyItNow } from '../product-card-actions/buy-it-now'
import { ProductCardDeleteFromWishlist } from '../product-card-actions/delete-wishlist'
import { ProductCardLinkProps, ProductCardLink } from '../product-card-actions/product-card-link'
import { useProductCardHover } from '../product-card-context/product-card-hover.context'
import { CardVariant, IProductCard, ProductCardTag } from '../product-card.types'

import { productSwiperScrollEventTrigger } from './product-image-section-helper'
import { useProductImageProps } from './product-image-section.hooks'
import styles from './product-image-section.module.scss'
import { ProductTags } from './product-tags'
import { useShowBuyNowBasedOnDeviceType } from './utils'

export interface ProductImageSectionProps extends ProductCardLinkProps {
  productCard: IProductCard
  selectedCardVariant?: CardVariant
  gender?: GenderName | undefined
  imageRef?: MutableRefObject<HTMLImageElement | null> | undefined
  maxWidth?: number
  eagerLoadImage?: boolean
  wishlistIconClassName?: string
  wishlistIconClass?: string
  addToCartClassName?: string
  showDeleteBtn?: boolean
  deleteItemClassName?: string
  quality?: number
  className?: string
  applicableTags?: ProductCardTag[]
  hideWishlistIcon?: boolean
  hideBuyNowIcon?: boolean
  hideTags?: boolean
  quickBuyEnabled?: boolean
  wishlistEnabled?: boolean
  readyToShipEnabled?: boolean
  siteSearchQuery?: string
  productPosition?: number
  productSearchObjectId?: string
  showSecondImageOnProductTiles?: boolean
  productClickTrackConfig?: TrackConfig
  wishlistTrackConfig?: Record<string, unknown>
  showMScroll?: boolean
  quickBuyOverride?: () => void
  forceShowBuyNow?: boolean
  tabData?: Record<string, string | boolean>
}

// @i18n-ignore
const PRODUCT_CARD_TRACKER = 'product_card'

export const ProductImageSection = (props: ProductImageSectionProps) => {
  const [keySwiper, setKeySwiper] = useState(0) // State to force remount
  const { isHovered, setHovered } = useProductCardHover()
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const DEFAULT_MAX_PRODUCT_CARD_WIDTH = isMobile ? MAX_MOBILE_WIDTH : MAX_DESKTOP_WIDTH / 2

  const swiperRef = useRef<SnapScrollHandle>(null)
  const productImageProps = useProductImageProps(
    props.selectedCardVariant,
    props.gender,
    isMobile,
    isHovered,
    props.showSecondImageOnProductTiles,
    props.showMScroll,
  )

  const handleOnScrollEvent = useCallback(productSwiperScrollEventTrigger(props.productCard), [productCard])

  const price = props.selectedCardVariant?.price?.salePrice
  const selectedColor = props.selectedCardVariant?.displayConfig.value
  const trackingContext = {
    product_price: price,
    clicked_prodid: `${props.productCard.product.productId}_${selectedColor}`,
  }
  const wishlistTrackingConfig = {
    productId: props.productCard.product.relatedIds.spid,
    color: selectedColor,
    trackerSection: PRODUCT_CARD_TRACKER,
    tags: props.applicableTags,
    product_search_query: props.siteSearchQuery,
    product_position: props.productPosition,
    context: {
      ...props.wishlistTrackConfig,
      ...trackingContext,
    },
  }

  const firstProductImageId = Array.isArray(productImageProps) ? productImageProps[0]?.id : productImageProps.id
  // The useEffect should only trigger when the active index changes,
  // and not for other interactions like hovering on desktop or clicking on mobile.
  // Simply including the product image array as a dependency may cause unnecessary re-renders.
  useEffect(() => {
    if (Array.isArray(productImageProps) && swiperRef.current?.getActiveIndex() !== 0) {
      setKeySwiper(keySwiper + 1)
    }
  }, [firstProductImageId])

  const showBuyNowBasedOnDeviceType = useShowBuyNowBasedOnDeviceType(isMobile)
  const showBuyNowBase = !props.hideBuyNowIcon && props.quickBuyEnabled && showBuyNowBasedOnDeviceType
  const showBuyNow = props.forceShowBuyNow ?? showBuyNowBase

  return (
    <div
      className={classnames(styles.imageSection, props.className)}
      onMouseEnter={() => {
        setHovered(true)
      }}
      onMouseLeave={() => {
        setHovered(false)
      }}
    >
      <div className={styles.imageContainer}>
        {Array.isArray(productImageProps) ? (
          <SnapScroll
            key={keySwiper}
            ref={swiperRef}
            spaceBetween={10}
            scrollEventDebounceTime={250}
            handleOnScrollEvent={handleOnScrollEvent}
            id={'product-image-section'}
          >
            {productImageProps.map((productImg: ImageProps) => {
              return (
                <ProductCardLink
                  key={productImg.id}
                  slug={props.slug}
                  additionalAttributes={props.additionalAttributes}
                  selectorKey={props.selectorKey}
                  tracker={props.tracker}
                  productCardLinkClass={props.productCardLinkClass}
                  selectedCardVariant={props.selectedCardVariant}
                  siteSearchQuery={props.siteSearchQuery}
                  productPosition={props.productPosition}
                  productSearchObjectId={props.productSearchObjectId}
                  title={props.title}
                  spid={props.spid}
                  reviewSummary={props.reviewSummary}
                  productClickTrackConfig={props.productClickTrackConfig}
                  colorVariants={props.colorVariants}
                  eddDetails={props.eddDetails}
                  product={props.product}
                  onClickCb={props.onClickCb}
                >
                  <Image
                    {...productImg}
                    // @i18n-ignore
                    alt={`${selectedColor} ${props.title}`}
                    quality={props.quality}
                    ref={props.imageRef}
                    className={styles.productImage}
                    maxWidth={props.maxWidth ?? DEFAULT_MAX_PRODUCT_CARD_WIDTH}
                    // @i18n-ignore
                    loading={'eager'}
                  />
                </ProductCardLink>
              )
            })}
          </SnapScroll>
        ) : (
          <Image
            {...productImageProps}
            // @i18n-ignore
            alt={`${selectedColor} ${props.title}`}
            quality={props.quality}
            ref={props.imageRef}
            className={styles.productImage}
            maxWidth={props.maxWidth ?? DEFAULT_MAX_PRODUCT_CARD_WIDTH}
            // @i18n-ignore
            loading={props.eagerLoadImage ? 'eager' : 'lazy'}
          />
        )}
      </div>

      <div className={styles.imageOverlay}>
        {showBuyNow ? (
          <ProductCardBuyItNow
            productCard={props.productCard}
            selectedCardVariant={props.selectedCardVariant}
            addToCartClassName={classnames(props.addToCartClassName, styles.buyItNowBtn)}
            siteSearchQuery={props.siteSearchQuery}
            productPosition={props.productPosition}
            productSearchObjectId={props.productSearchObjectId}
            productClickTrackConfig={props.productClickTrackConfig}
            quickBuyOverride={props.quickBuyOverride}
            tabData={props.tabData}
          />
        ) : null}
        {!props.hideWishlistIcon && props.wishlistEnabled && (
          <ProductCardAddToWishlist
            wishlistIconClassName={props.wishlistIconClassName}
            spidWishlist={props.productCard.product.relatedIds.spid}
            selectedCardVariant={props.selectedCardVariant}
            wishlistTrackingConfig={wishlistTrackingConfig}
            wishlistIconClass={props.wishlistIconClass}
            {...props}
          />
        )}
        {!isMobile && props.showDeleteBtn && props.wishlistEnabled ? (
          <ProductCardDeleteFromWishlist
            deleteItemClassName={classnames(props.deleteItemClassName, styles.deleteBtn)}
            spid={props.productCard.product.relatedIds.spid}
            selectedCardVariant={props.selectedCardVariant}
            wishlistTrackingConfig={wishlistTrackingConfig}
          />
        ) : null}
        {props.hideTags ? null : (
          <ProductTags
            applicableTags={props.applicableTags}
            readyToShip={props.readyToShipEnabled}
            className={styles.productTag}
            position={TAG_POSITION.BOTTOM}
          />
        )}
      </div>
    </div>
  )
}
